import { useMutation, useQueryClient } from '@tanstack/react-query'

import { FAVORITES } from '@pages/Trade/utils/constants'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { CreateWatchlistModel, WatchlistModel } from '@model/DxTrade.types'

import { sortWatchlistsByOrder } from '../Watchlists/helpers'

export const useCreateWatchlist = () => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreateWatchlistModel): Promise<WatchlistModel> =>
      apiClient.createWatchlist(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['trade', 'dxtrade', 'watchlists'] })

      const previousData = queryClient.getQueriesData<WatchlistModel[]>({
        queryKey: ['trade', 'dxtrade', 'watchlists'],
      })

      const optimisticWatchlist: WatchlistModel = {
        ...payload,
        id: -Date.now(),
        version: 0,
      }

      queryClient.setQueriesData<WatchlistModel[]>(
        { queryKey: ['trade', 'dxtrade', 'watchlists'] },
        (old) => [optimisticWatchlist, ...(old ?? [])]
      )

      return { previousData }
    },
    onSuccess: async (newWatchlist) => {
      const allCached = queryClient.getQueriesData<WatchlistModel[]>({
        queryKey: ['trade', 'dxtrade', 'watchlists'],
      })
      const cached = allCached.flatMap(([, data]) => data ?? [])
      const others = sortWatchlistsByOrder(
        cached.filter(
          (w) => w.id !== newWatchlist.id && w.name !== FAVORITES && w.id > 0 && w.type !== 'PUBLIC'
        )
      )
      const orderValues = [
        { id: newWatchlist.id, orderValue: 1 },
        ...others.map((w, i) => ({ id: w.id, orderValue: i + 2 })),
      ]
      try {
        await apiClient.bulkUpdateWatchlistOrderValues({ orderValues })
      } catch (e) {
        console.error('[useCreateWatchlist] Failed to update watchlist order values:', e)
      }
    },
    onError: (_err, _variables, context) => {
      context?.previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['trade', 'dxtrade', 'watchlists'] })
    },
  })
}
